import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { createContext, useContext, useMemo } from 'react'

import { useBoolean } from 'usehooks-ts'

import type {
  OrchestratedAuctionListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'

import usePlaceBidMutation from '@/queries/usePlaceBidMutation'

import { BidConfirmationModal } from './BidConfirmationModal'

type BidConfirmationModalContextValue = {
  openModal: () => void
  closeModal: () => void
  showModal: boolean
  isLoading: boolean
  error: unknown
}

const BidConfirmationModalContext =
  createContext<BidConfirmationModalContextValue>({
    showModal: false,
    openModal: () => null,
    closeModal: () => null,
    isLoading: false,
    error: null,
  })

export const BidConfirmationModalProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const {
    setFalse: closeModal,
    setTrue: openModal,
    value: showModal,
  } = useBoolean(false)
  const bidMutation = usePlaceBidMutation()
  const { isPending: isLoading, error } = bidMutation

  const value: BidConfirmationModalContextValue = {
    openModal,
    closeModal,
    showModal,
    isLoading,
    error,
  }

  return (
    <BidConfirmationModalContext.Provider value={value}>
      {children}
    </BidConfirmationModalContext.Provider>
  )
}

export const useBidConfirmationModal = () => {
  const { openModal, closeModal, showModal, isLoading, error } = useContext(
    BidConfirmationModalContext
  )

  const MemoizedModal = useMemo(() => {
    const Component = ({
      bidLocation,
      buyerAccountId,
      buyersPremium,
      doNotify,
      isSortFrozen,
      listing,
      recordBidPlacedWhileFrozen,
      selectedQuote,
      setIsPaused,
    }: {
      bidLocation: 'pdp' | 'list'
      buyerAccountId: string
      buyersPremium: number
      doNotify?: boolean
      isSortFrozen?: boolean
      listing: OrchestratedAuctionListingDetails
      recordBidPlacedWhileFrozen?: () => void
      selectedQuote?: OrchestratedQuote | null
      setIsPaused: Dispatch<SetStateAction<boolean>>
    }) =>
      showModal ? (
        <BidConfirmationModal
          bidLocation={bidLocation}
          buyerAccountId={buyerAccountId}
          buyersPremium={buyersPremium}
          closeModal={closeModal}
          doNotify={doNotify}
          error={error}
          isSortFrozen={isSortFrozen}
          isLoading={isLoading}
          listing={listing}
          recordBidPlacedWhileFrozen={recordBidPlacedWhileFrozen}
          selectedQuote={selectedQuote}
          setIsPaused={setIsPaused}
        />
      ) : null

    return Component
  }, [closeModal, error, isLoading, showModal])

  return {
    BidConfirmationModal: MemoizedModal,
    openModal,
    closeModal,
    showModal,
  }
}
